import Phaser from 'phaser';
import { OBJECTS, WORLD_CONFIG } from '../world.config.js';

const PROXIMITY_TILES = 2;

// Tracks objects in the active room. On each frame, finds the nearest
// in-range object to the drone, shows a HUD prompt, and dispatches on E.
export class ObjectManager {
  constructor(scene, droneSprite, roomId, { onModal, onDialogue, onLink, onEasterEgg, onTvPortfolio, onMail } = {}) {
    this.scene = scene;
    this.drone = droneSprite;
    this.roomId = roomId;
    this.handlers = { onModal, onDialogue, onLink, onEasterEgg, onTvPortfolio, onMail };
    this.tilePx = WORLD_CONFIG.tileSize * WORLD_CONFIG.scale;
    this.proximityPx = PROXIMITY_TILES * this.tilePx;

    this.activeId = null;
    this.lastDispatchTime = 0;
    this.dispatchCooldown = 350; // ms — prevents double-fire on key bounce
    this.suspended = false;

    this.eKey = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E);

    this.promptEl = this._buildPromptEl();
    this.setRoom(roomId);
  }

  setRoom(roomId) {
    this.roomId = roomId;
    // Objects participate in proximity detection if they have either:
    //   - `type` (interactive — shows [E] prompt and dispatches on E)
    //   - `showLabel: true` (label-only — shows the name tag, no E action)
    // Pure decoratives with descriptive `label` strings but neither flag
    // (rug, worktable surface, floor lamp) stay silent in proximity.
    this.objects = OBJECTS.filter((o) => o.room === roomId && (o.type || o.showLabel));
    this.activeId = null;
    if (this.promptEl) this.promptEl.style.display = 'none';
  }

  _buildPromptEl() {
    const existing = document.querySelector('.iw-prompt');
    if (existing) {
      existing.style.display = 'none';
      return existing;
    }
    const el = document.createElement('div');
    el.className = 'iw-prompt';
    el.style.cssText = `
      position: fixed;
      left: 50%;
      bottom: 90px;
      transform: translateX(-50%);
      padding: 6px 14px;
      background: rgba(15, 12, 10, 0.85);
      color: #f4ead5;
      border: 1px solid rgba(244, 234, 213, 0.25);
      border-radius: 999px;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      font-size: 12px;
      letter-spacing: 0.08em;
      display: none;
      z-index: 40;
      pointer-events: none;
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
      animation: iwPromptPulse 1.4s ease-in-out infinite;
    `;
    if (!document.getElementById('iw-prompt-style')) {
      const style = document.createElement('style');
      style.id = 'iw-prompt-style';
      style.textContent = `
        @keyframes iwPromptPulse {
          0%, 100% { transform: translateX(-50%) translateY(0) }
          50% { transform: translateX(-50%) translateY(-3px) }
        }
      `;
      document.head.appendChild(style);
    }
    document.body.appendChild(el);
    return el;
  }

  registerSprite(objectId, sprite, baseScale) {
    const obj = this.objects.find((o) => o.id === objectId);
    if (obj) {
      obj._sprite = sprite;
      obj._baseScale = baseScale;
    }
  }

  setSuspended(value) {
    this.suspended = value;
    if (value) this.promptEl.style.display = 'none';
    if (value) this._endActivePulse();
  }

  update() {
    if (this.suspended) return;

    const dx = this.drone.x;
    const dy = this.drone.y;
    let nearest = null;
    let nearestDist = Infinity;

    for (const obj of this.objects) {
      const ox = obj.x * this.tilePx + this.tilePx / 2;
      const oy = obj.y * this.tilePx + this.tilePx / 2;
      const dist = Phaser.Math.Distance.Between(dx, dy, ox, oy);
      if (dist <= this.proximityPx && dist < nearestDist) {
        nearest = obj;
        nearestDist = dist;
      }
    }

    if (nearest) {
      if (this.activeId !== nearest.id) {
        this._endActivePulse();
        this.activeId = nearest.id;
        const isInteractive = !!nearest.type;
        // Interactive: `[ E ]  Label` with bouncing pulse. Label-only:
        // just the label, no E prefix, no animation.
        this.promptEl.textContent = isInteractive
          ? `[ E ]  ${nearest.promptText ?? nearest.label ?? 'Interact'}`
          : (nearest.label ?? '');
        this.promptEl.style.animation = isInteractive ? '' : 'none';
        this.promptEl.style.display = 'block';
        if (isInteractive) this._beginActivePulse(nearest);
      }
      if (nearest.type && Phaser.Input.Keyboard.JustDown(this.eKey)) {
        const now = this.scene.time.now;
        if (now - this.lastDispatchTime > this.dispatchCooldown) {
          this.lastDispatchTime = now;
          this._dispatch(nearest);
        }
      }
    } else if (this.activeId !== null) {
      this._endActivePulse();
      this.activeId = null;
      this.promptEl.style.display = 'none';
    }
  }

  // Subtle scale pulse on the nearest interactive object so visitors can
  // see at a glance which object E will dispatch on. Uses _baseScale
  // captured at sprite-registration time so we don't snap to scale 1
  // (which would shrink scale-2 placeholder sprites).
  _beginActivePulse(obj) {
    if (!obj._sprite || obj._baseScale == null) return;
    const base = obj._baseScale;
    this._activeTween = this.scene.tweens.add({
      targets: obj._sprite,
      scaleX: base * 1.08,
      scaleY: base * 1.08,
      duration: 360,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this._activeSprite = obj._sprite;
    this._activeBaseScale = base;
  }

  _endActivePulse() {
    if (this._activeTween) {
      this._activeTween.stop();
      this._activeTween = null;
    }
    if (this._activeSprite && this._activeBaseScale != null) {
      this._activeSprite.setScale(this._activeBaseScale);
    }
    this._activeSprite = null;
    this._activeBaseScale = null;
  }

  _dispatch(obj) {
    switch (obj.type) {
      case 'modal':
        this.handlers.onModal?.(obj);
        break;
      case 'dialogue':
        this.handlers.onDialogue?.(obj);
        break;
      case 'link':
        this.handlers.onLink?.(obj);
        break;
      case 'easter_egg':
        this.handlers.onEasterEgg?.(obj);
        break;
      case 'tvPortfolio':
        this.handlers.onTvPortfolio?.(obj);
        break;
      case 'mail':
        this.handlers.onMail?.(obj);
        break;
      default:
        break;
    }
  }

  destroy() {
    this.promptEl?.remove();
    this.promptEl = null;
  }
}
